import { clientMoveIn } from "../../lib/clientMoveIn";
// src/components/clients/ClientCard.tsx
import React, { useState } from "react";
import { darken } from "@mui/material/styles";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Avatar,
  Chip,
  Stack,
  IconButton,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon as MuiListItemIcon,
  ListItemText,
  alpha,
  useTheme,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  Warning as WarningIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  AccessTime as AccessTimeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  NoteAdd as NoteAddIcon,
  Assignment as TaskIcon,
  PhoneDisabled as PhoneDisabledIcon,
  PersonOutline as PersonOutlineIcon,
} from "@mui/icons-material";
import { shadows } from "../../theme/theme";
import type { Client, ClientStatus } from "./types";
import { statusConfig, statusActions, priorityConfig, typeConfig, actionsByEmphasis } from "./config";
import { formatRelativeTime, isMobileDevice } from "./helpers";

interface ClientCardProps {
  client: Client;
  onEdit: (client: Client) => void;
  onDelete: (id: string) => void;
  onViewDetails: (client: Client) => void;
  onQuickAction: (action: string, client: Client) => void;
  onStatusChange: (client: Client, newStatus: ClientStatus) => void;
  showAgent?: boolean;
  /** TRAIN-4: client's phone has a STOP on record — texting is not permitted.
      Surfaced on the card so suppression is visible before any thread opens. */
  smsUnreachable?: boolean;
}

const ClientCard: React.FC<ClientCardProps> = ({
  client,
  onEdit,
  onDelete,
  onViewDetails,
  onQuickAction,
  onStatusChange,
  showAgent = true,
  smsUnreachable = false,
}) => {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(
    null
  );

  const statusInfo = statusConfig[client.status];
  const priorityInfo = priorityConfig[client.priority];
  const hoverAccentColor = darken(theme.palette.primary.dark, 0.35);
  const isJaneDemo =
    client.first_name === "Jane" && client.last_name === "Demo";
  const isMobile = isMobileDevice();
  const relabel = (a: { key: string; label: string; icon: React.ReactNode; activityType: string }) => {
    // Relabel text/call actions on desktop to reflect that they only log
    if (!isMobile && a.key === "sent_text") return { ...a, label: "Log Text" };
    if (!isMobile && a.key === "call") return { ...a, label: "Log Call" };
    // Shorter labels on mobile
    if (isMobile && a.key === "send_guest_card") return { ...a, label: "Guest Card" };
    return a;
  };
  // Status-specific inline actions (shown as buttons at the bottom of the card)
  // Emphasis order keeps each stage's two inline buttons as they were (PKT-0841).
  const actions = actionsByEmphasis(client.status).map(relabel);

  // Full action list for the 3-dot menu — union of all status actions, deduped by key.
  // Exclude sent_text and call since those log-only actions are handled separately.
  const menuActions = (() => {
    const seen = new Set<string>();
    const excluded = new Set(["sent_text", "call"]);
    const all: typeof actions = [];
    Object.values(statusActions).forEach((list) => {
      list.forEach((a) => {
        if (excluded.has(a.key)) return;
        if (!seen.has(a.key)) {
          seen.add(a.key);
          all.push(relabel(a));
        }
      });
    });
    return all;
  })();

  const handleCardOpen = () => {
    onViewDetails(client);
  };

  const handleCardKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleCardOpen();
    }
  };

  const stopCardClick = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  return (
    <Card
      onClick={handleCardOpen}
      onKeyDown={handleCardKeyDown}
      role="button"
      tabIndex={0}
      sx={{
        position: "relative",
        zIndex: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        boxShadow: shadows.card,
        borderColor: theme.palette.divider,
        borderTopWidth: 4,
        borderLeftWidth: 4,
        borderTopColor: statusInfo.color,
        borderLeftColor: statusInfo.color,
        transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: shadows.cardHover,
          zIndex: 2,
          borderTopColor: hoverAccentColor,
          borderLeftColor: hoverAccentColor,
          borderRightColor: alpha(hoverAccentColor, 0.35),
          borderBottomColor: alpha(hoverAccentColor, 0.35),
        },
        "&:focus-visible": {
          transform: "translateY(-2px)",
          boxShadow: shadows.cardHover,
          zIndex: 2,
          borderTopColor: hoverAccentColor,
          borderLeftColor: hoverAccentColor,
          borderRightColor: alpha(hoverAccentColor, 0.35),
          borderBottomColor: alpha(hoverAccentColor, 0.35),
        },
      }}
    >
      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", pl: 2.5, pt: 2 }}>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box>
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                {client.first_name} {client.last_name}
                {client.priority === "urgent" && (
                  <Tooltip title="Urgent">
                    <WarningIcon
                      sx={{ color: priorityInfo.color, fontSize: 18 }}
                    />
                  </Tooltip>
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {client.email}
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={(e) => {
              stopCardClick(e);
              setMenuAnchor(e.currentTarget);
            }}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>

        {/* Status & Tags */}
        <Stack
          direction="row"
          spacing={1}
          sx={{ mb: 2, flexWrap: "wrap", gap: 0.5 }}
        >
          <Chip
            size="small"
            label={statusInfo.label}
            icon={statusInfo.icon as React.ReactElement}
            onClick={(e) => {
              stopCardClick(e);
              setStatusMenuAnchor(e.currentTarget);
            }}
            sx={{
              backgroundColor: alpha(statusInfo.color, 0.15),
              color: statusInfo.color,
              fontWeight: 500,
              cursor: "pointer",
              "& .MuiChip-icon": { color: statusInfo.color },
            }}
          />
          {!isMobile && (
            <Chip
              size="small"
              label={typeConfig[client.client_type].label}
              variant="outlined"
            />
          )}
          {smsUnreachable && (
            <Chip
              size="small"
              variant="outlined"
              label="SMS opt-out"
              data-testid="sms-optout-badge"
              // `color="warning"` renders warning.main, which is 3.19:1 on the
              // light card surface — below AA for the one compliance signal a
              // human is meant to notice. The deeper shade clears it; dark
              // mode keeps the lighter one for the same reason.
              sx={{
                color: (t) =>
                  t.palette.mode === "light"
                    ? t.palette.warning.dark
                    : t.palette.warning.light,
                borderColor: (t) =>
                  t.palette.mode === "light"
                    ? t.palette.warning.dark
                    : t.palette.warning.light,
              }}
            />
          )}
          {client.max_budget && (
            <Chip
              size="small"
              label={`$${client.max_budget.toLocaleString()}`}
              icon={<MoneyIcon sx={{ fontSize: 14 }} />}
              variant="outlined"
            />
          )}
          {isMobile && showAgent && (
            <Chip
              size="small"
              avatar={
                client.assigned_agent ? (
                  <Avatar
                    src={client.assigned_agent.avatar_url}
                    sx={{ width: 20, height: 20, fontSize: "0.65rem" }}
                  >
                    {client.assigned_agent.full_name?.[0]}
                  </Avatar>
                ) : undefined
              }
              icon={
                client.assigned_agent ? undefined : (
                  <PersonOutlineIcon sx={{ fontSize: 14 }} />
                )
              }
              label={client.assigned_agent?.full_name || "Unassigned"}
              sx={{
                maxWidth: 140,
                borderRadius: 999,
                backgroundColor: client.assigned_agent
                  ? alpha(theme.palette.primary.main, 0.08)
                  : alpha(theme.palette.text.secondary, 0.08),
                color: client.assigned_agent
                  ? theme.palette.text.primary
                  : theme.palette.text.secondary,
                "& .MuiChip-label": {
                  px: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
            />
          )}
        </Stack>

        {/* Contact Info + Agent */}
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {client.phone && (
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <PhoneIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="body2">{client.phone}</Typography>
              </Box>
            )}
            {clientMoveIn(client) && (
              <Box display="flex" alignItems="center" gap={1}>
                <CalendarIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary">
                  {clientMoveIn(client)}
                </Typography>
              </Box>
            )}
          </Box>

          {!isMobile && showAgent && (
            <Chip
              size="small"
              icon={
                client.assigned_agent ? undefined : (
                  <PersonOutlineIcon sx={{ fontSize: 16 }} />
                )
              }
              avatar={
                client.assigned_agent ? (
                  <Avatar
                    src={client.assigned_agent.avatar_url}
                    sx={{ width: 22, height: 22, fontSize: "0.7rem" }}
                  >
                    {client.assigned_agent.full_name?.[0]}
                  </Avatar>
                ) : undefined
              }
              label={client.assigned_agent?.full_name || "Unassigned"}
              sx={{
                maxWidth: 160,
                flexShrink: 0,
                borderRadius: 999,
                backgroundColor: client.assigned_agent
                  ? alpha(theme.palette.primary.main, 0.08)
                  : alpha(theme.palette.text.secondary, 0.08),
                color: client.assigned_agent
                  ? theme.palette.text.primary
                  : theme.palette.text.secondary,
                "& .MuiChip-label": {
                  px: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
                "& .MuiChip-icon": {
                  color: "inherit",
                },
              }}
            />
          )}
        </Box>

        {/* Last Contact */}
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.default, 0.5),
            mb: 2,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <AccessTimeIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary">
              {client.last_contacted_at
                ? `Last contact: ${formatRelativeTime(
                    client.last_contacted_at
                  )}`
                : "Never contacted"}
            </Typography>
          </Box>
        </Box>

        {/* Quick Actions */}
        {actions.length > 0 && (
          <Box
            sx={{
              mt: "auto",
              display: "flex",
              gap: 1,
            }}
          >
            {actions.slice(0, 2).map((action) => (
              <Button
                key={action.key}
                size="small"
                variant="outlined"
                startIcon={action.icon}
                data-tour={
                  action.key === "send_list" && isJaneDemo
                    ? "jane-send-list-card-btn"
                    : undefined
                }
                onClick={(event) => {
                  stopCardClick(event);
                  onQuickAction(action.key, client);
                }}
                sx={{
                  flex: 1,
                  fontSize: "0.75rem",
                  textTransform: "none",
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                {action.label}
              </Button>
            ))}
          </Box>
        )}
      </CardContent>

      {/* More Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        MenuListProps={{
          onClick: stopCardClick,
          onKeyDown: stopCardClick,
        }}
      >
        <MenuItem
          onClick={(event) => {
            stopCardClick(event);
            onEdit(client);
            setMenuAnchor(null);
          }}
        >
          <MuiListItemIcon>
            <EditIcon fontSize="small" />
          </MuiListItemIcon>
          <ListItemText>Edit Client</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={(event) => {
            stopCardClick(event);
            onQuickAction("addNote", client);
            setMenuAnchor(null);
          }}
        >
          <MuiListItemIcon>
            <NoteAddIcon fontSize="small" />
          </MuiListItemIcon>
          <ListItemText>Add Note</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={(event) => {
            stopCardClick(event);
            onQuickAction("addTask", client);
            setMenuAnchor(null);
          }}
        >
          <MuiListItemIcon>
            <TaskIcon fontSize="small" />
          </MuiListItemIcon>
          <ListItemText>Add Task</ListItemText>
        </MenuItem>
        <Divider />
        {menuActions.map((action) => (
          <MenuItem
            key={action.key}
            onClick={(event) => {
              stopCardClick(event);
              onQuickAction(action.key, client);
              setMenuAnchor(null);
            }}
          >
            <MuiListItemIcon>{action.icon}</MuiListItemIcon>
            <ListItemText>{action.label}</ListItemText>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={(event) => {
            stopCardClick(event);
            onQuickAction("markOutOfContact", client);
            setMenuAnchor(null);
          }}
          sx={{ color: "warning.main" }}
        >
          <MuiListItemIcon>
            <PhoneDisabledIcon fontSize="small" color="warning" />
          </MuiListItemIcon>
          <ListItemText>Archive</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={(event) => {
            stopCardClick(event);
            onDelete(client.id);
            setMenuAnchor(null);
          }}
          sx={{ color: "error.main" }}
        >
          <MuiListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </MuiListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Status Change Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => setStatusMenuAnchor(null)}
        MenuListProps={{
          onClick: stopCardClick,
          onKeyDown: stopCardClick,
        }}
      >
        {Object.entries(statusConfig).map(([key, { label, color, icon }]) => (
          <MenuItem
            key={key}
            selected={client.status === key}
            onClick={(event) => {
              stopCardClick(event);
              onStatusChange(client, key as ClientStatus);
              setStatusMenuAnchor(null);
            }}
          >
            <MuiListItemIcon sx={{ color }}>{icon}</MuiListItemIcon>
            <ListItemText>{label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Card>
  );
};

export default ClientCard;
